import React, { useCallback, useEffect, useMemo } from 'react'
import { useSessionStorage } from 'react-use'
import clsx from 'clsx'
import { Descendant } from 'slate'

import {
  ColumnConditionValue,
  ConditionValue,
  IActionRepeaterValue,
  thousandFmtNoDecimal,
  TypographyGroupValue,
} from '@makyoapp/makyo-common'
import {
  BaseComponent,
  Button,
  ColorVariant,
  CornerRadius,
  getShapeClass,
  getTextColorStyles,
  getTypographyTokenStyle,
  MaterialIcon,
  RawSpacingInput,
  RenderText,
  SearchBar,
  Skeleton,
  SkeletonGroup,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentBrandkit, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { IRow } from '@/core/models/tables/row'
import { cn } from '@/lib/cn'
import { useBuilderAccordionContext } from '@/context/__project__/BuilderAccordionContext'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCheckVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { ComponentSettingKey, UserColumnFilterValue } from '@/shared/builder'
import { parseSpacing } from '@/shared/builder-utils'
import { MAX_ROWS_LIMIT } from '@/shared/constants'
import { useDebounceInput } from '@/shared/makyo-hooks'
import { getSafely } from '@/shared/safely'

import { useCompPagination } from '../../hooks/useCompPagination'
import { CompFilterV2 } from '../../shared/CompFilterV2'
import { CompHeadline } from '../../shared/CompHeadline'
import { CompPagination } from '../../shared/CompPagination'
import { BaseCompListProps } from './comp'

export interface CompListWrapperCoreProps {
  infos: CoreComponentInfos
  brandkit: CoreComponentBrandkit
  tableData: CoreComponentTableData
  children: (props: BaseCompListProps) => React.ReactNode
}

/**
 * CompListWrapperCore
 */
export const CompListWrapperCore = ({
  infos: { component, containerClient, settings, detailTableId, detailRowIndex, currentParentBg },
  brandkit: { mode },
  tableData: { dbClient, dbTable: table, language, userSpecific },
  children,
}: CompListWrapperCoreProps) => {
  const { checkVisibility } = useCheckVisibilityV2()
  const { simulateAction } = useBuilderActionContextV2()
  const { setAccordions } = useBuilderAccordionContext()
  const { setActiveComponentAccordion } = useAppBuilderContext()

  const compId = component._id

  // ========================= data =========================

  // pagination
  const datasetLimit = settings[ComponentSettingKey.ListDataSetLimit]
  const isLimited = datasetLimit?.enabled
  const limit = datasetLimit?.value ?? 0
  const maxPerItems = isLimited ? limit : MAX_ROWS_LIMIT

  // const maxPerItems = getSafely(settings, [ComponentSettingKey.ListDataSetLimit], PAGINATION_CONSTANTS.DEFAULT_PER_PAGE)
  const columnsCount = settings[ComponentSettingKey.ListDesignNumCols] ?? 1
  const numRows = (settings[ComponentSettingKey.ListDesignNumRows] as number) ?? 1

  const orientation = settings[ComponentSettingKey.ListDesignOrientation]
  const isHoriz = orientation === 'horizontal'

  // pagination state
  const { pagination, setPagination } = useCompPagination({
    compId,
    defaultPerPage: isHoriz ? maxPerItems : Math.min(columnsCount * numRows, maxPerItems),
  })

  // data filter settings
  const filterSetting = settings[ComponentSettingKey.ListDataFilter] as ColumnConditionValue | undefined
  const sortBy = settings[ComponentSettingKey.ListDataSortBy] as string
  const sortType = settings[ComponentSettingKey.ListDataSortType] as string

  // search
  const [debouncedSearch, , , currentSearch, setCurrentSearch] = useDebounceInput('')

  // user filter state
  const [userFilter, setUserFilter] = useSessionStorage(`userFilter-${compId}`, {})

  // get initial rows
  const [{ rows: _rows, filteredRowsCount, allRowsCount }, isLoading] = useCompTableData({
    table,
    componentId: component._id,
    pagination,
    filterSetting,
    sortSetting: { sortType, sortBy },
    search: debouncedSearch,
    userFilter,
  })

  const slicedRows = useMemo(() => {
    const totalRows = (pagination.page + 1) * pagination.perPage
    const sliceOffset = totalRows > maxPerItems ? pagination.perPage - (totalRows - maxPerItems) : pagination.perPage
    return _rows.slice(0, sliceOffset)
  }, [pagination, _rows, maxPerItems])

  const rows = isHoriz ? slicedRows : _rows

  // create a stable row indexes map
  // todo: use state instead
  const rowIndexesMap = useMemo(() => {
    return table.GetRowIndexesMap(language, userSpecific)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, table, userSpecific, filteredRowsCount]) // allRowsCount is needed to trigger re-render

  const totalPages = useMemo(() => {
    return Math.ceil(filteredRowsCount / pagination.perPage)
  }, [filteredRowsCount, pagination.perPage])

  // reset pagination if current page is greater than total pages
  // @note only in builder mode, it might happen after performing a search or filter
  useEffect(() => {
    if (totalPages !== 0 && pagination.page !== 0 && pagination.page > totalPages) {
      setPagination(0, pagination.perPage)
    }
  }, [totalPages, pagination, setPagination])

  // user (manual) filter settings
  const userFilterSetting: Array<UserColumnFilterValue> = useMemo(() => {
    const stg = settings[ComponentSettingKey.ListBehaviorUserFilter] as UserColumnFilterValue[]
    return Array.isArray(stg) ? stg : []
  }, [settings])

  // groupBy
  const groupBy = settings[ComponentSettingKey.ListDataGroupBy] as string

  // map groupBy to entries using object.groupBy
  const groupEntries: [string, IRow[]][] | undefined = useMemo(() => {
    if (!groupBy) return
    const groupByCol = table.columnsMap.get(groupBy)
    if (!groupByCol) return

    return Object.entries(Object.groupBy(rows, (item) => item[groupByCol._id].toString()) as Record<string, IRow[]>)
    //
  }, [groupBy, rows, table.columnsMap])

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.ListDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.ListDesignCompHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey.ListDesignMargin] as RawSpacingInput

  const showPagination = settings[ComponentSettingKey.ListDesignPagination]

  // search box
  const searchAlign = settings[ComponentSettingKey.ListDesignGroupSearchAlign] ?? 'center'
  const searchBgColor = settings?.[ComponentSettingKey.ListDesignSearchBgColor] ?? 'primary-90'
  const searchCornerRadius = settings[ComponentSettingKey.ListDesignSearchCorner] as CornerRadius
  const searchMargin = settings[ComponentSettingKey.ListDesignSearchMargin] as RawSpacingInput
  const searchWidth = settings[ComponentSettingKey.ListDesignSearchWidth]?.[0] ?? '100%'
  const searchWidthValue = Number(String(searchWidth).replace(/[^0-9.]/g, '')) || 100
  const searchWidthUnit = String(searchWidth).replace(/[0-9.]/g, '') || '%'

  // filter
  const filterOverlayBgColor = settings[ComponentSettingKey.ListDesignFilterBgColor] ?? 'primary-95'
  const filterChipColor = settings[ComponentSettingKey.ListDesignFilterChipColor] ?? 'primary-95'
  const filterCornerRadius = settings[ComponentSettingKey.ListDesignFilterCornerRadius] as CornerRadius
  const filterPosition = settings[ComponentSettingKey.ListBehaviorUserFilterPosition] ?? 'top'

  // add button
  const addButtonColor = settings[ComponentSettingKey.ListDesignAddButtonColor] ?? 'primary-20'
  const addButtonCornerRadius = settings[ComponentSettingKey.ListDesignAddButtonCornerRadius] as CornerRadius
  const addButtonPosition = settings[ComponentSettingKey.ListBehaviorAddButtonPosition] ?? 'inline'

  // typography
  const typoSettings = settings[ComponentSettingKey.ListDesignTypography] as TypographyGroupValue

  // pagination style
  const paginationAlign = settings[ComponentSettingKey.ListDesignPaginationAlignment] ?? 'center'

  // group header
  const groupHeaderTypo = getSafely(typoSettings, [ComponentSettingKey.ListDesignGroupHeaderTypography], {
    typography: 'Title Medium',
  })
  const groupHeaderAlign = settings[ComponentSettingKey?.ListDesignGroupHeaderAlign]
  const groupHeaderMargin = parseSpacing(settings[ComponentSettingKey.ListDesignGroupHeaderMargin])
  const groupHeaderTypoStyle = getTypographyTokenStyle(groupHeaderTypo)

  const imageSize = settings[ComponentSettingKey.ListDesignImageWidth]
  const showListPagination = settings[ComponentSettingKey.ListDesignPagination] as boolean

  // ========================= actions =========================

  const addButtonClickActions = settings[ComponentSettingKey.ListActionAddButtonClick] as IActionRepeaterValue[]

  const handleAddButtonClick = () => {
    simulateAction({
      actions: addButtonClickActions,
      tableId: table.id,
    })
  }

  // ========================= behavior =========================
  const withSearch = settings[ComponentSettingKey.ListBehaviorUserSearch] as boolean
  const withSearchResultsInfo = settings[ComponentSettingKey.ListBehaviorSearchResultsInfo] as boolean

  const withAddButton = settings[ComponentSettingKey.ListBehaviorAddButtonShow] as boolean
  const addButtonVisibility = settings[ComponentSettingKey.ListBehaviorAddButtonVisibility] as
    | ConditionValue
    | undefined

  const showAddButton =
    withAddButton &&
    checkVisibility({
      visibility: addButtonVisibility,
      layerTableId: table.id,
    })

  // ========================= component title / headline =========================
  const headline = (settings[ComponentSettingKey.ListDataCompTitle] as Descendant[]) ?? []
  const headlineMargin = settings[ComponentSettingKey.ListDesignHeadlineMargin]
  const headlineTypo = typoSettings[ComponentSettingKey.ListDesignHeadlineTypography]
  const compTitleAlign = settings[ComponentSettingKey.ListDesignCompTitleAlign]

  // color
  // comp title
  const compTitleColor = settings[ComponentSettingKey.ListDesignCompTitleColor] || 'adaptive'
  const compTitleColorSolid = settings[ComponentSettingKey.ListDesignCompTitleColorSolid] as ColorVariant
  const compTitleColorStartGradient = settings[
    ComponentSettingKey.ListDesignCompTitleColorStartGradient
  ] as ColorVariant
  const compTitleColorEndGradient = settings[ComponentSettingKey.ListDesignCompTitleColorEndGradient] as ColorVariant
  const compTitleColorGradientDirection = settings[ComponentSettingKey.ListDesignCompTitleColorGradientDirection]

  // group header
  const groupHeaderColor = settings[ComponentSettingKey.ListDesignGroupHeaderColor] || 'adaptive'
  const groupHeaderColorSolid = settings[ComponentSettingKey.ListDesignGroupHeaderColorSolid] as ColorVariant
  const groupHeaderColorStartGradient = settings[
    ComponentSettingKey.ListDesignGroupHeaderColorStartGradient
  ] as ColorVariant
  const groupHeaderColorEndGradient = settings[
    ComponentSettingKey.ListDesignGroupHeaderColorEndGradient
  ] as ColorVariant
  const groupHeaderColorGradientDirection = settings[ComponentSettingKey.ListDesignGroupHeaderColorGradientDirection]

  // ========================= render =========================
  const shouldShowPagination = showPagination && !isHoriz && totalPages > 1 && filteredRowsCount > pagination.perPage

  const getCompListProps = useCallback(
    (items: IRow[]): BaseCompListProps => {
      return {
        settings,
        compId,
        table,
        rowIndexesMap,
        items,
        mode,
        pageBgColor: currentParentBg,
        component,
      }
    },
    [compId, table, rowIndexesMap, mode, currentParentBg, settings, component]
  )

  // if there are no rows after filtering, don't show the component
  const noDataToShow = !!filterSetting && allRowsCount === 0
  if (noDataToShow) return null

  const showSearchResultsInfo = withSearchResultsInfo && !isLoading && withSearch && debouncedSearch.trim() !== ''

  return (
    <BaseComponent
      id={component._id}
      width={width}
      alignment={alignment}
      margin={margin}
      backgroundColor={currentParentBg}
      className={clsx('box-border', isHoriz && 'max-w-full overflow-x-auto')}
    >
      {(rows.length > 0 || withSearch) && (
        <CompHeadline
          containerClient={containerClient}
          detailTableId={detailTableId}
          detailRowIndex={detailRowIndex}
          compTitleAlign={compTitleAlign}
          headline={headline}
          headlineMargin={headlineMargin}
          headlineTypo={headlineTypo}
          colorType={compTitleColor}
          backgroundColor={currentParentBg}
          styles={{
            ...getTextColorStyles(
              compTitleColor,
              compTitleColorSolid || 'primary-40',
              compTitleColorStartGradient || 'primary-40',
              compTitleColorEndGradient || 'primary-40',
              compTitleColorGradientDirection,
              currentParentBg ?? 'primary-90'
            ),
          }}
          onClick={() => {
            if (component?._id) setAccordions('data', component._id, `data-${ComponentSettingKey.ListDataCompTitle}`)
            setActiveComponentAccordion(`data-${ComponentSettingKey.ListDataCompTitle}`)
          }}
        />
      )}

      {filterPosition === 'top' && (
        <CompFilterV2
          id={compId}
          value={userFilter}
          setValue={setUserFilter}
          settings={userFilterSetting}
          table={table}
          filterChipColor={filterChipColor}
          filterOverlayBgColor={filterOverlayBgColor}
          filterCornerRadius={filterCornerRadius}
          filterMargin={settings[ComponentSettingKey.ListDesignFilterMargin]}
          inline={false}
        />
      )}

      <div className='flex items-center gap-2 mb-4'>
        {withSearch && (
          <SearchBar
            value={currentSearch}
            onChange={(e) => {
              setCurrentSearch(e.target.value)
              setPagination(0, pagination.perPage)
            }}
            onClear={() => {
              setCurrentSearch('')
            }}
            backgroundColor={searchBgColor}
            customWidth={{
              type: searchWidthUnit === '%' ? 'percentage' : 'pixel',
              value: searchWidthValue,
            }}
            // margin={searchMargin}
            borderStyles={{
              radius: searchCornerRadius,
            }}
            align={searchAlign}
            inputClassName='py-2.5'
          />
        )}

        <div className='flex min-w-0 items-center gap-1.5'>
          {filterPosition === 'inline' && (
            <CompFilterV2
              id={compId}
              value={userFilter}
              setValue={setUserFilter}
              settings={userFilterSetting}
              table={table}
              filterChipColor={filterChipColor}
              filterOverlayBgColor={filterOverlayBgColor}
              filterCornerRadius={filterCornerRadius}
              filterMargin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              inline={true}
            />
          )}

          {showAddButton && addButtonPosition === 'inline' && (
            <button
              className={clsx(
                'p-2 min-h-10 min-w-10 flex items-center justify-center cursor-pointer outline-none ring-0 focus:ring-0 focus:outline-none',
                addButtonColor === currentParentBg && `border border-on-${addButtonColor}/50`,
                getShapeClass(addButtonCornerRadius),
                `bg-${addButtonColor} text-on-${addButtonColor} hover:bg-${addButtonColor}/90`
              )}
              onClick={handleAddButtonClick}
            >
              <MaterialIcon icon='add' size={20} className={`text-on-${addButtonColor}`} />
            </button>
          )}
        </div>
      </div>

      {showSearchResultsInfo && (
        <div
          className={cn('px-1 mb-4 text-sm', `text-on-${currentParentBg}/80`)}
        >{`Showing ${thousandFmtNoDecimal.format(filteredRowsCount)} results of ${thousandFmtNoDecimal.format(allRowsCount)}`}</div>
      )}

      {isLoading ? (
        <SkeletonGroup backgroundColor={currentParentBg!}>
          <div className='space-y-4'>
            {[...new Array(4)].map((_, i) => (
              <Skeleton key={i} className='w-full h-12 rounded-md' />
            ))}
          </div>
        </SkeletonGroup>
      ) : groupEntries ? (
        <div className='flex flex-col gap-4'>
          {groupEntries.map(([k, groupedRows]) => {
            return (
              <div key={k} className='flex flex-col gap-4'>
                <div
                  style={{
                    ...groupHeaderTypoStyle,
                    ...groupHeaderMargin,
                    textAlign: groupHeaderAlign,
                    color: `var(--color-on-${currentParentBg})`,
                  }}
                >
                  <RenderText
                    colorType={groupHeaderColor}
                    colorSolid={groupHeaderColorSolid}
                    colorStartGradient={groupHeaderColorStartGradient}
                    colorEndGradient={groupHeaderColorEndGradient}
                    colorGradientDirection={groupHeaderColorGradientDirection}
                    value={k}
                    alignment={groupHeaderAlign}
                    backgroundColor={currentParentBg || 'neutral-100'}
                    limit={10}
                    typography={groupHeaderTypo}
                    className='w-full'
                  />
                  {/* <span
                    className={cn(
                      groupHeaderColor === 'gradient'
                        ? '!bg-clip-text'
                        : !groupHeaderColor || groupHeaderColor === 'adaptive'
                          ? `text-[var(--color-on-${currentParentBg})]`
                          : ''
                    )}
                    style={{
                      ...getTextColorStyles(
                        groupHeaderColor,
                        groupHeaderColorSolid,
                        groupHeaderColorStartGradient,
                        groupHeaderColorEndGradient,
                        groupHeaderColorGradientDirection,
                        currentParentBg ?? 'primary-90'
                      ),
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                    }}
                  >
                    {k}
                  </span> */}
                </div>

                {children(getCompListProps(groupedRows))}
              </div>
            )
          })}
        </div>
      ) : rows.length > 0 ? (
        children(getCompListProps(rows))
      ) : null}

      {showAddButton && addButtonPosition === 'bottom' && (
        <div className='mt-4'>
          <Button
            colorVariant={addButtonColor}
            fullWidth
            onClick={handleAddButtonClick}
            icon={<MaterialIcon icon='add' size={20} />}
          >
            Add Item
          </Button>
        </div>
      )}

      {shouldShowPagination && (
        <div className={clsx('flex w-full py-4 pagination')} style={{ justifyContent: paginationAlign }}>
          <CompPagination
            totalPages={totalPages}
            perPage={pagination.perPage}
            page={pagination.page}
            onChange={setPagination}
            backgroundColor={currentParentBg}
          />
        </div>
      )}
    </BaseComponent>
  )
}
